import React, { useState } from 'react';
import { Copy, Download, Loader, CheckCircle, AlertCircle } from 'lucide-react';
import {
  submitAutoRegistration,
  copyToClipboard,
  downloadCredentials,
  formatRegistrationResponse,
} from '../../services/autoRegisterService';

const AutoRegisterForm = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
  });

  const [isLoading, setIsLoading] = useState(false);
  const [registrationResult, setRegistrationResult] = useState(null);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [copiedField, setCopiedField] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // 清除错误和成功消息
    setError(null);
    setSuccessMessage(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // 验证输入
    if (!formData.firstName.trim() || !formData.lastName.trim()) {
      setError('请填写名字和姓氏');
      return;
    }

    setIsLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const response = await submitAutoRegistration(
        formData.firstName,
        formData.lastName
      );
      const formatted = formatRegistrationResponse(response);
      setRegistrationResult(formatted);
      setSuccessMessage('注册凭证已生成！请妥善保管这些信息。');

      // 清空表单
      setFormData({
        firstName: '',
        lastName: '',
      });
    } catch (err) {
      // 🆕 处理积分不足错误
      if (err.message && err.message.includes('积分不足')) {
        setError('积分不足，请前往积分中心充值');
      } else {
        setError(err.message || '注册失败，请重试');
      }
      console.error('注册错误:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopy = async (text, fieldName) => {
    const success = await copyToClipboard(text);
    if (success) {
      setCopiedField(fieldName);
      setTimeout(() => setCopiedField(null), 2000);
    }
  };

  const handleDownload = () => {
    if (registrationResult) {
      downloadCredentials(registrationResult);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* 标题部分 */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            US Visa 账户自动注册
          </h1>
          <p className="text-gray-600">
            只需输入名字和姓氏，系统自动生成 usvisascheduling.com 账户凭证
          </p>
        </div>

        {/* 主容器 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* 左侧：注册表单 */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">
              请输入您的信息
            </h2>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* 名字输入 */}
              <div>
                <label
                  htmlFor="firstName"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  名字 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  placeholder="例如：John"
                  disabled={isLoading}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
              </div>

              {/* 姓氏输入 */}
              <div>
                <label
                  htmlFor="lastName"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  姓氏 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  placeholder="例如：Doe"
                  disabled={isLoading}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
              </div>

              {/* 错误消息 */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-red-900">错误</p>
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                </div>
              )}

              {/* 成功消息 */}
              {successMessage && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-green-900">成功</p>
                    <p className="text-sm text-green-700">{successMessage}</p>
                  </div>
                </div>
              )}

              {/* 提交按钮 */}
              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-semibold py-3 px-4 rounded-lg transition duration-200 flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <Loader className="w-5 h-5 animate-spin" />
                    生成中...
                  </>
                ) : (
                  '生成注册凭证'
                )}
              </button>

              {/* 🆕 积分消费提示 */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <div className="text-blue-600 text-2xl">💰</div>
                  <div>
                    <p className="text-sm font-medium text-blue-900">积分消费提示</p>
                    <p className="text-sm text-blue-700 mt-1">
                      每次生成账户凭证需要消耗 <strong>5 积分</strong>。点击"生成注册凭证"即表示您同意扣除积分。
                    </p>
                    <p className="text-xs text-blue-600 mt-2">
                      💡 如果生成失败，积分将自动退还到您的账户。
                    </p>
                  </div>
                </div>
              </div>

              {/* 信息提示 */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-900">
                  💡 <strong>提示:</strong> 系统将自动为您生成强密码和安全问题答案，请妥善保管生成的凭证。
                </p>
              </div>
            </form>
          </div>

          {/* 右侧：结果显示 */}
          <div>
            {registrationResult ? (
              <div className="bg-white rounded-lg shadow-lg p-6 space-y-6">
                <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                  <CheckCircle className="w-6 h-6 text-green-500" />
                  注册凭证
                </h2>

                {/* 用户名 */}
                <CredentialField
                  label="用户名"
                  value={registrationResult.username}
                  fieldName="username"
                  copiedField={copiedField}
                  onCopy={handleCopy}
                />

                {/* 密码 */}
                <CredentialField
                  label="密码"
                  value={registrationResult.password}
                  fieldName="password"
                  copiedField={copiedField}
                  onCopy={handleCopy}
                  isPassword
                />

                {/* 邮箱 */}
                <CredentialField
                  label="邮箱"
                  value={registrationResult.email}
                  fieldName="email"
                  copiedField={copiedField}
                  onCopy={handleCopy}
                />

                {/* 任务 ID */}
                <CredentialField
                  label="任务 ID"
                  value={registrationResult.taskId}
                  fieldName="taskId"
                  copiedField={copiedField}
                  onCopy={handleCopy}
                  isMonospace
                />

                {/* 安全问题 */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">
                    安全问题和答案
                  </h3>
                  <div className="space-y-3">
                    {registrationResult.securityQuestions.map(
                      (qa, index) => (
                        <div key={index} className="border border-gray-200 rounded p-3">
                          <p className="text-sm font-medium text-gray-700 mb-2">
                            {index + 1}. {qa.question}
                          </p>
                          <div className="flex items-center justify-between gap-2">
                            <p className="text-sm text-gray-900 font-mono bg-gray-50 px-3 py-2 rounded flex-1">
                              {qa.answer}
                            </p>
                            <button
                              onClick={() =>
                                handleCopy(
                                  qa.answer,
                                  `answer_${index}`
                                )
                              }
                              className="p-2 hover:bg-gray-100 rounded transition"
                              title="复制答案"
                            >
                              <Copy
                                className={`w-4 h-4 ${
                                  copiedField === `answer_${index}`
                                    ? 'text-green-500'
                                    : 'text-gray-500'
                                }`}
                              />
                            </button>
                          </div>
                        </div>
                      )
                    )}
                  </div>
                </div>

                {/* 下载按钮 */}
                <button
                  onClick={handleDownload}
                  className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-4 rounded-lg transition duration-200 flex items-center justify-center gap-2"
                >
                  <Download className="w-5 h-5" />
                  下载凭证
                </button>

                {/* 新注册按钮 */}
                <button
                  onClick={() => {
                    setRegistrationResult(null);
                    setFormData({ firstName: '', lastName: '' });
                  }}
                  className="w-full bg-gray-300 hover:bg-gray-400 text-gray-800 font-semibold py-3 px-4 rounded-lg transition duration-200"
                >
                  新注册
                </button>

                {/* 警告信息 */}
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <p className="text-sm text-yellow-900">
                    ⚠️ <strong>重要:</strong> 请将这些凭证保存到安全的地方。我们不会再显示密码。
                  </p>
                </div>
              </div>
            ) : (
              <div className="bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 p-8 text-center">
                <p className="text-gray-500 text-sm">
                  生成的凭证将显示在这里
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * 凭证字段组件
 */
const CredentialField = ({
  label,
  value,
  fieldName,
  copiedField,
  onCopy,
  isPassword = false,
  isMonospace = false,
}) => {
  return (
    <div>
      <label className="text-sm font-semibold text-gray-900 mb-2 block">
        {label}
      </label>
      <div className="flex items-center gap-2">
        <input
          type={isPassword ? 'password' : 'text'}
          value={value}
          readOnly
          className={`flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-900 select-all ${
            isMonospace ? 'font-mono text-xs' : ''
          }`}
        />
        <button
          onClick={() => onCopy(value, fieldName)}
          className="p-2 hover:bg-gray-100 rounded transition"
          title="复制"
        >
          <Copy
            className={`w-5 h-5 ${
              copiedField === fieldName ? 'text-green-500' : 'text-gray-500'
            }`}
          />
        </button>
      </div>
      {copiedField === fieldName && (
        <p className="text-xs text-green-600 mt-1">已复制</p>
      )}
    </div>
  );
};

export default AutoRegisterForm;
